import {
  Box,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  Text,
  Flex,
} from '@chakra-ui/react';
import { defaultIconSize, Icons } from '../Icons';
import { useTranslation } from 'react-i18next';
import { ImageLoadingState } from './ImageLoadingState';

type HoverVariant = 'label' | 'scale';

interface Props {
  identifier: string;
  children: React.ReactNode;
  hoverMenu?: React.ReactNode;
  variant?: HoverVariant;
}

export function ExpandImage({
  children,
  identifier,
  variant = 'label',
  hoverMenu,
}: Props) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { t } = useTranslation();

  return (
    <>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        pos="relative"
        role="group"
        cursor="zoom-in"
        {...(variant === 'scale'
          ? {
              transition: 'transform 200ms ease',
              _hover: {
                transform: 'scale(1.05)',
              },
            }
          : null)}
      >
        {children}
        {variant === 'label' && (
          <Box
            pos="absolute"
            left={0}
            right={0}
            bottom={0}
            transition="opacity 200ms ease, bottom 200ms ease"
            opacity={0}
            _groupHover={{ opacity: 1, bottom: 4 }}
          >
            <Flex
              margin="auto"
              align="center"
              justify="center"
              textAlign="center"
              gap={2}
              backgroundColor="blackAlpha.800"
              padding={2}
              w="fit-content"
              _light={{
                backgroundColor: 'whiteAlpha.800',
              }}
            >
              <Icons.ArrowExpand boxSize={defaultIconSize} />
              <Text fontWeight="bold">{t('fileBrowser.clickToExpand')}</Text>
            </Flex>
          </Box>
        )}
      </Box>
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <InnerModal identifier={identifier} hoverMenu={hoverMenu} />
      </Modal>
    </>
  );
}

function InnerModal({
  identifier,
  hoverMenu,
}: {
  identifier: string;
  hoverMenu: React.ReactNode;
}) {
  return (
    <ModalContent w="fit-content" role="group">
      <ModalCloseButton bg="whiteAlpha.400" color="white" />
      <ImageLoadingState
        minW="250px"
        minH="250px"
        identifier={identifier}
        size={640}
      />
      {hoverMenu}
    </ModalContent>
  );
}
